import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { ChevronLeft, Share, X } from 'lucide-react'

/**
 * Read-only renderer for BlockNote block trees.
 *
 * Reading is most of the traffic, so the read path deliberately does NOT
 * mount BlockNote — this walks the JSON directly. The editor (and its chunk)
 * loads only when someone taps Edit. Unknown block types degrade to
 * paragraphs: in a reference repository, losing a line is worse than
 * mis-styling one.
 */

export default function BlockRenderer({ blocks, onToggleCheck }) {
  if (!blocks?.length) return null
  // Checklist items are addressed by depth-first ordinal, not block id —
  // imported blocks don't reliably carry ids. Ordinals are precomputed in
  // one walk (matching toggleNthChecklist in PageDetailPage) and looked up
  // by block reference at render time: counting during render breaks under
  // React's double-render in dev, and render order is React's business.
  const ords = new Map()
  let n = 0
  const walk = (arr) =>
    arr.forEach((b) => {
      if (b.type === 'checkListItem') ords.set(b, n++)
      if (b.children?.length) walk(b.children)
    })
  walk(blocks)
  const ctx = { ords, onToggleCheck }
  return <div className="space-y-1 text-[15px] leading-relaxed text-ink">{renderList(blocks, ctx)}</div>
}

function renderList(blocks, ctx) {
  const out = []
  let i = 0
  while (i < blocks.length) {
    const b = blocks[i]
    // Consecutive list items of one kind group into a single <ul>/<ol>.
    if (b.type === 'bulletListItem' || b.type === 'numberedListItem') {
      const kind = b.type
      const items = []
      while (i < blocks.length && blocks[i].type === kind) {
        items.push(blocks[i])
        i++
      }
      const ListTag = kind === 'bulletListItem' ? 'ul' : 'ol'
      out.push(
        <ListTag
          key={b.id || i}
          className={kind === 'bulletListItem' ? 'list-disc space-y-1 pl-6' : 'list-decimal space-y-1 pl-6'}
        >
          {items.map((item, n) => (
            <li key={item.id || n}>
              <Inline content={item.content} />
              {item.children?.length > 0 && <div className="mt-1">{renderList(item.children, ctx)}</div>}
            </li>
          ))}
        </ListTag>
      )
      continue
    }
    // Runs of file attachments become a grid of document tiles — a shelf
    // of rectangles reads faster than a column of filenames (Charles,
    // 2026-08-02, the Distributable PDFs page).
    if (b.type === 'file') {
      const files = []
      while (i < blocks.length && blocks[i].type === 'file') {
        files.push(blocks[i])
        i++
      }
      out.push(
        <div key={b.id || i} className="my-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {files.map((f, n) => (
            <FileTile key={f.id || n} props={f.props || {}} />
          ))}
        </div>
      )
      continue
    }
    out.push(<Block key={b.id || i} block={b} ctx={ctx} />)
    i++
  }
  return out
}

// Same-origin files must open in-app, never by navigating (Charles,
// 2026-08-04: tapping an .xls stranded him on the raw file — the installed
// app opens in-scope links in its own webview, which has no browser chrome
// and no way back; the image lightbox exists for the same reason).
// External URLs keep target=_blank: out-of-scope links get iOS's in-app
// Safari sheet with a Done button, which is fine.
const isInAppFile = (u) => typeof u === 'string' && u.startsWith('/')

/**
 * Full-screen document viewer: header with Back + share, iframe body.
 * iOS previews pdf/xls/docx in the iframe via Quick Look; the share button
 * opens the system sheet (Open in Safari / Save to Files) for anything the
 * preview can't do.
 */
function FileViewer({ url, name, onClose }) {
  const abs = new URL(url, window.location.href).href
  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-white" onClick={(e) => e.stopPropagation()}>
      <div
        className="flex shrink-0 items-center gap-2 border-b border-hairline px-2 pb-2"
        style={{ paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))' }}
      >
        <button type="button" onClick={onClose} className="tap flex items-center pr-1 text-sm font-medium text-navy">
          <ChevronLeft size={22} strokeWidth={2.2} /> Back
        </button>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{name}</span>
        {typeof navigator.share === 'function' && (
          <button
            type="button"
            aria-label="Share or save"
            onClick={() => navigator.share({ url: abs }).catch(() => {})}
            className="tap flex items-center justify-center px-2 text-ink"
          >
            <Share size={18} />
          </button>
        )}
      </div>
      <iframe src={url} title={name} className="min-h-0 w-full flex-1 border-0 bg-grouped" />
    </div>,
    document.body
  )
}

function FileTile({ props }) {
  const name = props.name || props.caption || 'Attachment'
  const ext = (/\.([a-z0-9]{1,5})$/i.exec(props.name || props.url || '')?.[1] || 'file').toUpperCase()
  const [open, setOpen] = useState(false)
  const cls = 'tap flex flex-col gap-2 rounded-[10px] border border-hairline bg-white p-3 active:bg-grouped'
  const face = (
    <>
      {/* a little portrait "document" with the type on its face */}
      <span className="flex h-11 w-9 items-center justify-center rounded-[3px] border-[1.5px] border-ink text-[9px] font-bold tracking-wide text-ink">
        {ext}
      </span>
      <span className="break-words text-xs font-medium leading-snug text-ink">{name}</span>
    </>
  )
  if (!isInAppFile(props.url)) {
    return (
      <a href={props.url} target="_blank" rel="noreferrer" className={cls}>
        {face}
      </a>
    )
  }
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={cls + ' text-left'}>
        {face}
      </button>
      {open && <FileViewer url={props.url} name={name} onClose={() => setOpen(false)} />}
    </>
  )
}

/** The 📎 attachment row — in-app viewer for same-origin files, like FileTile. */
function FileRow({ props }) {
  const name = props.name || props.caption || 'Attachment'
  const [open, setOpen] = useState(false)
  const cls = 'tap my-1 flex items-center gap-2 border border-ink px-4 py-3 text-sm font-medium text-ink active:bg-grouped'
  if (!isInAppFile(props.url)) {
    return (
      <a href={props.url} target="_blank" rel="noreferrer" className={cls}>
        📎 {name}
      </a>
    )
  }
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={cls + ' w-full text-left'}>
        📎 {name}
      </button>
      {open && <FileViewer url={props.url} name={name} onClose={() => setOpen(false)} />}
    </>
  )
}

function Block({ block, ctx }) {
  const { type, props = {}, content, children } = block
  const kids = children?.length > 0 && <div className="ml-4 mt-1">{renderList(children, ctx)}</div>

  switch (type) {
    case 'heading': {
      const Tag = { 1: 'h2', 2: 'h3', 3: 'h4' }[props.level] || 'h3'
      const cls = {
        h2: 'mt-6 mb-1 text-xl font-semibold',
        h3: 'mt-5 mb-1 text-lg font-semibold',
        h4: 'mt-4 mb-1 text-base font-semibold',
      }[Tag]
      return (
        <>
          <Tag className={cls}>
            <Inline content={content} />
          </Tag>
          {kids}
        </>
      )
    }
    case 'checkListItem': {
      // Checklists are live for editors: ticking saves straight through
      // without entering the editor (Charles, 2026-08-02 — to-do pages like
      // Wire Maintenance are used mid-task, both hands full).
      const ord = ctx.ords.get(block)
      const toggle = ctx.onToggleCheck
      return (
        <>
          <div className="flex items-start gap-2.5 py-0.5">
            <input
              type="checkbox"
              checked={!!props.checked}
              readOnly={!toggle}
              onChange={toggle ? () => toggle(ord) : undefined}
              onClick={(e) => e.stopPropagation()}
              className={'mt-1 h-4 w-4 shrink-0 accent-ink' + (toggle ? ' cursor-pointer' : '')}
            />
            <span className={props.checked ? 'text-muted line-through' : undefined}>
              <Inline content={content} />
            </span>
          </div>
          {kids}
        </>
      )
    }
    case 'quote':
      return (
        <blockquote className="border-l-[3px] border-link bg-grouped py-2 pl-4 pr-3 text-ink/80">
          <Inline content={content} />
        </blockquote>
      )
    case 'codeBlock':
      return (
        <pre className="overflow-x-auto rounded-xl bg-grouped p-4 font-mono text-[13px]">
          <code>{textOf(content)}</code>
        </pre>
      )
    case 'divider':
      return <hr className="my-5 border-hairline" />
    case 'image':
      return <SmartImage url={props.url} caption={props.caption} />
    case 'video':
      return (
        <figure className="my-3">
          <video src={props.url} controls preload="metadata" className="w-full border-[1.5px] border-ink" />
          {props.caption && <figcaption className="mt-1 text-xs text-muted">{props.caption}</figcaption>}
        </figure>
      )
    case 'audio':
      return (
        <figure className="my-3">
          <audio src={props.url} controls preload="none" className="w-full" />
          {props.caption && <figcaption className="mt-1 text-xs text-muted">{props.caption}</figcaption>}
        </figure>
      )
    case 'file':
      return <FileRow props={props} />
    case 'table':
      return <Table content={content} />
    default:
      // paragraph + anything unknown
      return (
        <>
          <p className="min-h-[1em] py-0.5">
            <Inline content={content} />
          </p>
          {kids}
        </>
      )
  }
}

function Table({ content }) {
  const rows = content?.rows || []
  if (rows.length === 0) return null
  const [head, ...body] = rows
  const cellContent = (cell) => <Inline content={Array.isArray(cell) ? cell : cell?.content} />
  return (
    <div className="my-3 scroll-x border-[1.5px] border-ink">
      <table className="w-full min-w-[480px] border-collapse text-sm">
        <thead>
          <tr>
            {(head.cells || []).map((c, i) => (
              <th key={i} className="border-b border-ink px-3 py-2 text-left font-semibold">
                {cellContent(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r} className="odd:bg-white even:bg-grouped/40">
              {(row.cells || []).map((c, i) => (
                <td key={i} className="border-b border-hairline px-3 py-2 align-top">
                  {cellContent(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Phone-friendly images. The import stored 320/800/1600 derivatives beside
 * every large original with a predictable name, so try the 800px derivative
 * first and fall back to the original if there isn't one. The 15 MB
 * screenshots never travel unless someone explicitly opens the original.
 */
function SmartImage({ url, caption }) {
  const [open, setOpen] = useState(false)

  // Esc closes, and the page behind the lightbox doesn't scroll.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open])

  if (!url) return null
  const variant = url.replace(/\.(png|jpe?g|webp|heic|tiff?)$/i, '-800.jpg')
  return (
    <figure className="my-3">
      {/* In-app lightbox, never a raw navigation: the installed app has no
          browser chrome, so opening the file directly stranded people on
          the image with no way back (Charles, 2026-08-02). */}
      <button type="button" onClick={() => setOpen(true)} className="block cursor-zoom-in">
        <img
          src={variant}
          onError={(e) => {
            if (e.currentTarget.src !== new URL(url, location.href).href) e.currentTarget.src = url
          }}
          alt={caption || ''}
          loading="lazy"
          className="max-h-[560px] border-[1.5px] border-ink"
        />
      </button>
      {caption && <figcaption className="mt-1 text-xs text-muted">{caption}</figcaption>}

      {/* Portaled to <body>: inside the shell's scroll pane iOS treats
          fixed as absolute, and the tab bar painted over the overlay. */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
          onClick={(e) => {
            // stopPropagation: this click closes the lightbox — it must not
            // reach the page's click-to-edit wrapper underneath. (Portal
            // events still bubble through the React tree, so this matters.)
            e.stopPropagation()
            setOpen(false)
          }}
        >
          <button
            type="button"
            aria-label="Close"
            className="absolute right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white"
            style={{ top: 'calc(12px + env(safe-area-inset-top, 0px))' }}
          >
            <X size={22} />
          </button>
          {/* full original in the lightbox — the 800px derivative is for the page flow */}
          <img src={url} alt={caption || ''} className="max-h-[92dvh] max-w-[96vw] object-contain" />
        </div>,
        document.body
      )}
    </figure>
  )
}

function Inline({ content }) {
  if (!Array.isArray(content)) return null
  return content.map((node, i) => {
    if (node.type === 'link') {
      const href = node.href || ''
      const label = <Inline content={node.content} />
      // Media links in prose open the in-app viewer — handing them to the
      // SPA router (no such route) or the webview (stranded) both fail.
      if (href.startsWith('/media/')) {
        return <InlineFileLink key={i} href={href} label={label} />
      }
      // Internal links (rewritten by the importer) stay in the SPA.
      return href.startsWith('/') ? (
        <Link key={i} to={href} className="text-link hover:underline">
          {label}
        </Link>
      ) : (
        <a key={i} href={href} target="_blank" rel="noreferrer" className="break-all text-link hover:underline">
          {label}
        </a>
      )
    }
    if (node.type !== 'text') return null
    let el = node.text
    const s = node.styles || {}
    if (s.code) el = <code className="rounded bg-grouped px-1.5 py-0.5 font-mono text-[13px]">{el}</code>
    if (s.bold) el = <strong>{el}</strong>
    if (s.italic) el = <em>{el}</em>
    if (s.strike) el = <s>{el}</s>
    return <span key={i}>{el}</span>
  })
}

/** A prose link to a stored file — looks like a link, opens the viewer. */
function InlineFileLink({ href, label }) {
  const [open, setOpen] = useState(false)
  const name = decodeURIComponent(href.split('/').pop() || 'Attachment')
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="break-all text-link hover:underline">
        {label}
      </button>
      {open && <FileViewer url={href} name={name} onClose={() => setOpen(false)} />}
    </>
  )
}

function textOf(content) {
  if (!Array.isArray(content)) return ''
  return content.map((n) => (n.type === 'text' ? n.text : textOf(n.content))).join('')
}
